export interface PrinterProfile {
	id: string;
	name: string;
	ownerName: string;
	avatar: string;
	bio: string;
	location: { lat: number; lng: number; city: string; state: string };
	printerModel: string;
	materials: Material[];
	maxBuildSize: { x: number; y: number; z: number };
	rating: number;
	reviewCount: number;
	reputationScore: number;
	completedJobs: number;
	avgPrintTime: number; // hours
	responseTime: string;
	samplePhotos: string[];
	verified: boolean;
	joinedDate: string;
	specialties: string[];
	pricePerGram: Record<string, number>;
}

export type Material = "PLA" | "ABS" | "PETG" | "TPU" | "Nylon" | "Resin" | "ASA" | "Carbon Fiber";

export type OrderStatus = "pending" | "accepted" | "printing" | "ready" | "completed" | "cancelled";

export interface Order {
	id: string;
	printerId: string;
	printerName: string;
	fileName: string;
	fileSize: number;
	material: Material;
	color: string;
	quantity: number;
	estimatedPrice: number;
	finalPrice: number;
	status: OrderStatus;
	createdAt: string;
	updatedAt: string;
	estimatedCompletion: string;
	notes: string;
	dimensions: { x: number; y: number; z: number };
	weight: number; // grams
	escrowHeld: boolean;
	trackingHistory: { status: OrderStatus; timestamp: string; note: string }[];
}

export interface Review {
	id: string;
	printerId: string;
	orderId: string;
	authorName: string;
	authorAvatar: string;
	rating: number;
	qualityRating: number;
	speedRating: number;
	communicationRating: number;
	comment: string;
	photos: string[];
	createdAt: string;
	helpful: number;
}

export interface PriceEstimate {
	material: Material;
	weightGrams: number;
	baseCost: number;
	materialCost: number;
	laborCost: number;
	platformFee: number;
	total: number;
	estimatedHours: number;
}

// Seed data for demo
const samplePhotos = [
	"https://images.unsplash.com/photo-1581092160607-ee22621dd758?w=400&h=300&fit=crop",
	"https://images.unsplash.com/photo-1581092162384-8987c1d64718?w=400&h=300&fit=crop",
	"https://images.unsplash.com/photo-1581092795360-fd1ca04f0952?w=400&h=300&fit=crop",
	"https://images.unsplash.com/photo-1612815154858-60aa4c59eaa6?w=400&h=300&fit=crop",
];

export const printers: PrinterProfile[] = [
	{
		id: "p1",
		name: "MakerSpace Pro",
		ownerName: "Alex Chen",
		avatar: "",
		bio: "Professional 3D printing service with 5+ years experience. Specializing in functional prototypes and engineering parts.",
		location: { lat: 37.7749, lng: -122.4194, city: "San Francisco", state: "CA" },
		printerModel: "Prusa MK4S",
		materials: ["PLA", "PETG", "ABS", "TPU"],
		maxBuildSize: { x: 250, y: 210, z: 220 },
		rating: 4.9,
		reviewCount: 127,
		reputationScore: 98,
		completedJobs: 342,
		avgPrintTime: 6,
		responseTime: "< 1 hour",
		samplePhotos,
		verified: true,
		joinedDate: "2021-03-15",
		specialties: ["Prototyping", "Mechanical Parts", "Cosplay Props"],
		pricePerGram: { PLA: 0.08, PETG: 0.12, ABS: 0.1, TPU: 0.15 },
	},
	{
		id: "p2",
		name: "PrintHub SF",
		ownerName: "Maria Garcia",
		avatar: "",
		bio: "High-quality resin and FDM printing. Fast turnaround and great communication. We love bringing your ideas to life!",
		location: { lat: 37.7849, lng: -122.4094, city: "San Francisco", state: "CA" },
		printerModel: "Bambu Lab X1 Carbon",
		materials: ["PLA", "ABS", "PETG", "Nylon", "Carbon Fiber"],
		maxBuildSize: { x: 256, y: 256, z: 256 },
		rating: 4.8,
		reviewCount: 89,
		reputationScore: 95,
		completedJobs: 215,
		avgPrintTime: 4,
		responseTime: "< 30 min",
		samplePhotos,
		verified: true,
		joinedDate: "2022-01-10",
		specialties: ["Industrial Parts", "Multi-Color", "Large Format"],
		pricePerGram: { PLA: 0.07, ABS: 0.09, PETG: 0.11, Nylon: 0.18, "Carbon Fiber": 0.35 },
	},
	{
		id: "p3",
		name: "ResinWorks Studio",
		ownerName: "James Park",
		avatar: "",
		bio: "Specializing in high-detail resin prints for miniatures, jewelry, and dental models. Ultra-fine layer resolution.",
		location: { lat: 37.7649, lng: -122.4294, city: "San Francisco", state: "CA" },
		printerModel: "Formlabs Form 3+",
		materials: ["Resin"],
		maxBuildSize: { x: 145, y: 145, z: 185 },
		rating: 4.95,
		reviewCount: 203,
		reputationScore: 99,
		completedJobs: 512,
		avgPrintTime: 8,
		responseTime: "< 2 hours",
		samplePhotos,
		verified: true,
		joinedDate: "2020-06-22",
		specialties: ["Miniatures", "Jewelry", "Dental", "High Detail"],
		pricePerGram: { Resin: 0.25 },
	},
	{
		id: "p4",
		name: "QuickPrint Bay",
		ownerName: "Sarah Kim",
		avatar: "",
		bio: "Fast and affordable FDM printing. Same-day service available for small parts. Student-friendly pricing.",
		location: { lat: 37.7949, lng: -122.3994, city: "San Francisco", state: "CA" },
		printerModel: "Creality K1 Max",
		materials: ["PLA", "PETG", "TPU"],
		maxBuildSize: { x: 300, y: 300, z: 300 },
		rating: 4.6,
		reviewCount: 56,
		reputationScore: 88,
		completedJobs: 98,
		avgPrintTime: 3,
		responseTime: "< 1 hour",
		samplePhotos,
		verified: true,
		joinedDate: "2023-09-01",
		specialties: ["Fast Turnaround", "Budget Prints", "Large Parts"],
		pricePerGram: { PLA: 0.05, PETG: 0.08, TPU: 0.12 },
	},
	{
		id: "p5",
		name: "Precision Prints Co",
		ownerName: "David Thompson",
		avatar: "",
		bio: "Engineering-grade prints with tight tolerances. ISO certified workshop. Specializing in functional prototypes and end-use parts.",
		location: { lat: 37.7549, lng: -122.4394, city: "San Francisco", state: "CA" },
		printerModel: "Ultimaker S5",
		materials: ["PLA", "ABS", "PETG", "Nylon", "ASA"],
		maxBuildSize: { x: 330, y: 240, z: 300 },
		rating: 4.7,
		reviewCount: 145,
		reputationScore: 93,
		completedJobs: 428,
		avgPrintTime: 7,
		responseTime: "< 3 hours",
		samplePhotos,
		verified: true,
		joinedDate: "2019-11-05",
		specialties: ["Engineering", "Tight Tolerances", "End-Use Parts"],
		pricePerGram: { PLA: 0.09, ABS: 0.11, PETG: 0.13, Nylon: 0.2, ASA: 0.14 },
	},
	{
		id: "p6",
		name: "Creative Layer",
		ownerName: "Yuki Tanaka",
		avatar: "",
		bio: "Artistic 3D printing studio. Custom figurines, art installations, and creative projects. Let's make something beautiful.",
		location: { lat: 37.7699, lng: -122.4444, city: "San Francisco", state: "CA" },
		printerModel: "Prusa XL",
		materials: ["PLA", "PETG", "TPU", "ABS"],
		maxBuildSize: { x: 360, y: 360, z: 360 },
		rating: 4.85,
		reviewCount: 78,
		reputationScore: 96,
		completedJobs: 189,
		avgPrintTime: 10,
		responseTime: "< 2 hours",
		samplePhotos,
		verified: true,
		joinedDate: "2022-04-18",
		specialties: ["Art", "Figurines", "Custom Design", "Multi-Material"],
		pricePerGram: { PLA: 0.08, PETG: 0.12, TPU: 0.16, ABS: 0.1 },
	},
];

export const reviews: Review[] = [
	{
		id: "r1", printerId: "p1", orderId: "o1", authorName: "Mike R.", authorAvatar: "",
		rating: 5, qualityRating: 5, speedRating: 5, communicationRating: 5,
		comment: "Incredible quality on my mechanical parts. Perfect fit and finish. Alex was very communicative throughout.",
		photos: [], createdAt: "2024-12-15", helpful: 12,
	},
	{
		id: "r2", printerId: "p1", orderId: "o2", authorName: "Linda K.", authorAvatar: "",
		rating: 5, qualityRating: 5, speedRating: 4, communicationRating: 5,
		comment: "Great prototype prints. Layer lines were minimal and the dimensional accuracy was spot on.",
		photos: [], createdAt: "2024-12-10", helpful: 8,
	},
	{
		id: "r3", printerId: "p2", orderId: "o3", authorName: "Tom S.", authorAvatar: "",
		rating: 5, qualityRating: 5, speedRating: 5, communicationRating: 4,
		comment: "The multi-color print came out amazing. Very fast turnaround with the Bambu Lab printer.",
		photos: [], createdAt: "2024-12-20", helpful: 15,
	},
	{
		id: "r4", printerId: "p3", orderId: "o4", authorName: "Emma W.", authorAvatar: "",
		rating: 5, qualityRating: 5, speedRating: 4, communicationRating: 5,
		comment: "The detail on my miniatures is unbelievable. Best resin prints I've ever seen.",
		photos: [], createdAt: "2024-12-18", helpful: 22,
	},
	{
		id: "r5", printerId: "p4", orderId: "o5", authorName: "Chris D.", authorAvatar: "",
		rating: 4, qualityRating: 4, speedRating: 5, communicationRating: 4,
		comment: "Super fast delivery and great price. Quality is good for the price point. Will order again!",
		photos: [], createdAt: "2024-12-22", helpful: 6,
	},
	{
		id: "r6", printerId: "p5", orderId: "o6", authorName: "Rachel M.", authorAvatar: "",
		rating: 5, qualityRating: 5, speedRating: 4, communicationRating: 5,
		comment: "Engineering-grade quality. The tolerances on my functional parts were perfect.",
		photos: [], createdAt: "2024-12-25", helpful: 18,
	},
	{
		id: "r7", printerId: "p1", orderId: "o7", authorName: "Jake T.", authorAvatar: "",
		rating: 4, qualityRating: 5, speedRating: 3, communicationRating: 5,
		comment: "Top quality but took a bit longer than expected. Alex kept me updated which I appreciated.",
		photos: [], createdAt: "2024-11-28", helpful: 4,
	},
	{
		id: "r8", printerId: "p6", orderId: "o8", authorName: "Sophia L.", authorAvatar: "",
		rating: 5, qualityRating: 5, speedRating: 5, communicationRating: 5,
		comment: "The custom figurine Yuki created was absolutely stunning. True artistry in 3D printing.",
		photos: [], createdAt: "2024-12-30", helpful: 25,
	},
];

export const orders: Order[] = [
	{
		id: "ord-001",
		printerId: "p1",
		printerName: "MakerSpace Pro",
		fileName: "gear_assembly_v3.stl",
		fileSize: 2400000,
		material: "PETG",
		color: "Black",
		quantity: 2,
		estimatedPrice: 24.5,
		finalPrice: 24.5,
		status: "printing",
		createdAt: "2025-01-10T10:00:00Z",
		updatedAt: "2025-01-11T14:30:00Z",
		estimatedCompletion: "2025-01-13T10:00:00Z",
		notes: "Need tight tolerances on the gear teeth",
		dimensions: { x: 80, y: 80, z: 45 },
		weight: 62,
		escrowHeld: true,
		trackingHistory: [
			{ status: "pending", timestamp: "2025-01-10T10:00:00Z", note: "Order placed" },
			{ status: "accepted", timestamp: "2025-01-10T11:15:00Z", note: "Printer accepted your order" },
			{ status: "printing", timestamp: "2025-01-11T14:30:00Z", note: "Print started — estimated 8 hours" },
		],
	},
	{
		id: "ord-002",
		printerId: "p3",
		printerName: "ResinWorks Studio",
		fileName: "dragon_miniature.stl",
		fileSize: 5200000,
		material: "Resin",
		color: "Grey",
		quantity: 1,
		estimatedPrice: 18.75,
		finalPrice: 18.75,
		status: "completed",
		createdAt: "2024-12-28T09:00:00Z",
		updatedAt: "2025-01-02T16:00:00Z",
		estimatedCompletion: "2025-01-02T12:00:00Z",
		notes: "High detail, 0.025mm layer height please",
		dimensions: { x: 45, y: 35, z: 70 },
		weight: 28,
		escrowHeld: false,
		trackingHistory: [
			{ status: "pending", timestamp: "2024-12-28T09:00:00Z", note: "Order placed" },
			{ status: "accepted", timestamp: "2024-12-28T10:30:00Z", note: "Printer accepted your order" },
			{ status: "printing", timestamp: "2024-12-29T08:00:00Z", note: "Print started — ultra-fine detail mode" },
			{ status: "ready", timestamp: "2025-01-01T14:00:00Z", note: "Print complete, post-processing done" },
			{ status: "completed", timestamp: "2025-01-02T16:00:00Z", note: "Picked up by customer" },
		],
	},
	{
		id: "ord-003",
		printerId: "p2",
		printerName: "PrintHub SF",
		fileName: "phone_case_custom.stl",
		fileSize: 890000,
		material: "TPU",
		color: "Blue",
		quantity: 3,
		estimatedPrice: 15.0,
		finalPrice: 0,
		status: "accepted",
		createdAt: "2025-01-12T15:00:00Z",
		updatedAt: "2025-01-12T16:20:00Z",
		estimatedCompletion: "2025-01-14T10:00:00Z",
		notes: "Flexible phone case, matte finish preferred",
		dimensions: { x: 155, y: 75, z: 12 },
		weight: 35,
		escrowHeld: true,
		trackingHistory: [
			{ status: "pending", timestamp: "2025-01-12T15:00:00Z", note: "Order placed" },
			{ status: "accepted", timestamp: "2025-01-12T16:20:00Z", note: "Printer accepted — will start tomorrow" },
		],
	},
];

export function calculatePrice(
	material: Material,
	weightGrams: number,
	printerId: string
): PriceEstimate {
	const printer = printers.find((p) => p.id === printerId);
	const pricePerGram = printer?.pricePerGram[material] ?? 0.1;
	const materialCost = weightGrams * pricePerGram;
	const laborCost = Math.max(5, weightGrams * 0.03);
	const baseCost = 2.0;
	const subtotal = baseCost + materialCost + laborCost;
	const platformFee = subtotal * 0.1;
	const total = subtotal + platformFee;
	const estimatedHours = Math.max(1, weightGrams / 15);

	return {
		material,
		weightGrams,
		baseCost,
		materialCost: Math.round(materialCost * 100) / 100,
		laborCost: Math.round(laborCost * 100) / 100,
		platformFee: Math.round(platformFee * 100) / 100,
		total: Math.round(total * 100) / 100,
		estimatedHours: Math.round(estimatedHours * 10) / 10,
	};
}

export function getDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
	const R = 3959; // miles
	const dLat = ((lat2 - lat1) * Math.PI) / 180;
	const dLng = ((lng2 - lng1) * Math.PI) / 180;
	const a =
		Math.sin(dLat / 2) * Math.sin(dLat / 2) +
		Math.cos((lat1 * Math.PI) / 180) *
			Math.cos((lat2 * Math.PI) / 180) *
			Math.sin(dLng / 2) *
			Math.sin(dLng / 2);
	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
	return Math.round(R * c * 10) / 10;
}
